/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {reInitializeState} from '@ion/applications/src/helpers';
import {Account} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {PageState} from '@/types/pages';
import {AdminAction, AsyncState} from '@/types/redux';
import {
  ADD_ADMIN_GROUP,
  ADD_ADMIN_GROUP_ERROR,
  ADD_ADMIN_GROUP_REQUEST,
  ADD_ADMIN_GROUP_RESPONSE,
  ADMIN_GROUPS_GRID_REFRESHED,
  AUDIT_ADMIN_GROUP,
  AUDIT_ADMIN_GROUP_ERROR,
  AUDIT_ADMIN_GROUP_REQUEST,
  AUDIT_ADMIN_GROUP_RESPONSE,
  DELETE_ADMIN_GROUP,
  DELETE_ADMIN_GROUP_ERROR,
  DELETE_ADMIN_GROUP_REQUEST,
  DELETE_ADMIN_GROUP_RESPONSE,
  EDIT_ADMIN_GROUP,
  EDIT_ADMIN_GROUP_ERROR,
  EDIT_ADMIN_GROUP_REQUEST,
  EDIT_ADMIN_GROUP_RESPONSE,
  GET_ADMIN_GROUPS_ERROR,
  GET_ADMIN_GROUPS_REQUEST,
  GET_ADMIN_GROUPS_RESPONSE,
  IMPORT_ADMIN_GROUPS,
  INITIALIZE_ADMIN_GROUPS,
  RE_INITIALIZE_ADMIN_GROUPS,
  SET_ADMIN_GROUPS_GRID_FILTER_MODEL,
  SET_GRID_COLUMNS,
  UPDATE_GRID_COLUMNS
} from '../actions/admin-groups';
import {
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '../helpers';

export interface AdminGroupsAction extends AdminAction {
  group?: Account;
  groups?: Account[];
};

export interface AdminGroupsActionState extends AsyncState {
  selected: Account | Account[] | null;
  result: Account | Account[] | null;
  isEditing: boolean;
};

interface AdminGroupImportState {
  isImporting: boolean;
  result: Account | Account[] | null;
};
export interface AdminGroupsState extends PageState {
  groups: {
    error: ServiceError;
    loading: boolean;
  },
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    filterModel: FilterModel | {}
  },
  add: AdminGroupsActionState,
  edit: AdminGroupsActionState,
  destroy: AdminGroupsActionState,
  audit: AdminGroupsActionState,
  bulkImport: AdminGroupImportState,
};

// defines the user preference key for the admin groups page ...
const PREFERENCE = 'admin-groups';

// defines the initial state for the reducer ...
export const initialState: AdminGroupsState = {
  isInitialized : false,
  groups        : {
    error   : null,
    loading : false
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    filterModel         : {}
  },
  add : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  edit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  destroy : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  audit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  bulkImport : {
    isImporting : false,
    result      : null
  }
};

// defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_ADMIN_GROUPS] : draft => {
    draft.isInitialized            = true;
    const preference               = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;
  },
  [RE_INITIALIZE_ADMIN_GROUPS] : draft => {
    reInitializeState(draft, {initialState});
  },
  [SET_GRID_COLUMNS] : ({grid, groups}: AdminGroupsState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : groups.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [SET_ADMIN_GROUPS_GRID_FILTER_MODEL] : ({grid}: AdminGroupsState, {model}) => {
    grid.filterModel = model;
  },
  [UPDATE_GRID_COLUMNS] : ({grid}: AdminGroupsState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },

  [GET_ADMIN_GROUPS_REQUEST] : ({groups}: AdminGroupsState) => {
    groups.loading = true;
  },
  [GET_ADMIN_GROUPS_RESPONSE] : ({groups}: AdminGroupsState) => {
    groups.loading = false;
  },
  [GET_ADMIN_GROUPS_ERROR] : ({groups}: AdminGroupsState, {error}: AdminGroupsAction) => {
    groups.loading = false;
    groups.error   = error;
  },
  [ADD_ADMIN_GROUP] : ({add}: AdminGroupsState, {show}: AdminGroupsAction) => {
    add.isEditing = show;
    add.result    = null;
  },
  [ADD_ADMIN_GROUP_REQUEST] : ({add}: AdminGroupsState) => {
    add.loading = true;
    add.error   = add.result = null;
  },
  [ADD_ADMIN_GROUP_RESPONSE] : ({add}: AdminGroupsState, {group}: AdminGroupsAction) => {
    add.loading   = false;
    add.isEditing = false;
    add.result    = group;
  },
  [ADD_ADMIN_GROUP_ERROR] : ({add}: AdminGroupsState, {error}: AdminGroupsAction) => {
    add.loading   = false;
    add.error     = error;
  },

  [EDIT_ADMIN_GROUP] : ({edit}: AdminGroupsState, {group, show}: AdminGroupsAction) => {
    edit.selected  = show ? group : null;
    edit.isEditing = show;
    edit.result    = null;
  },
  [EDIT_ADMIN_GROUP_REQUEST] : ({edit}: AdminGroupsState) => {
    edit.loading = true;
    edit.error   = edit.result = null;
  },
  [EDIT_ADMIN_GROUP_RESPONSE] : ({edit}: AdminGroupsState, {group}: AdminGroupsAction) => {
    edit.loading   = false;
    edit.isEditing = false;
    edit.result    = group;
  },
  [EDIT_ADMIN_GROUP_ERROR] : ({edit}: AdminGroupsState, {error}: AdminGroupsAction) => {
    edit.loading   = false;
    edit.error     = error;
  },
  [DELETE_ADMIN_GROUP] : ({destroy}: AdminGroupsState, {group, show}: AdminGroupsAction) => {
    destroy.selected  = show ? group : null;
    destroy.isEditing = show;
    destroy.result    = null;
  },
  [DELETE_ADMIN_GROUP_REQUEST] : ({destroy}: AdminGroupsState) => {
    destroy.loading = true;
    destroy.error   = destroy.result = null;
  },
  [DELETE_ADMIN_GROUP_RESPONSE] : ({destroy}: AdminGroupsState, {group}: AdminGroupsAction) => {
    destroy.loading   = false;
    destroy.isEditing = false;
    destroy.result    = group;
  },
  [DELETE_ADMIN_GROUP_ERROR] : ({destroy}: AdminGroupsState, {error}: AdminGroupsAction) => {
    destroy.loading = false;
    destroy.error   = error;
    destroy.result  = null;
  },
  [AUDIT_ADMIN_GROUP] : ({audit}: AdminGroupsState, {group, show}: AdminGroupsAction) => {
    audit.selected  = show ? group : null;
    audit.isEditing = show;
    audit.result    = null;
  },
  [AUDIT_ADMIN_GROUP_REQUEST] : ({audit}: AdminGroupsState) => {
    audit.loading = true;
    audit.error   = audit.result = null;
  },
  [AUDIT_ADMIN_GROUP_RESPONSE] : ({audit}: AdminGroupsState, {result}: AdminGroupsAction) => {
    audit.loading   = false;
    audit.isEditing = false;
    audit.result    = result;
  },
  [AUDIT_ADMIN_GROUP_ERROR] : ({audit}: AdminGroupsState, {error}: AdminGroupsAction) => {
    audit.loading   = false;
    audit.error     = error;
  },
  [IMPORT_ADMIN_GROUPS] : ({bulkImport}: AdminGroupsState, {show, result}: AdminGroupsAction) => {
    bulkImport.isImporting = show;
    bulkImport.result = result;
  },
  [ADMIN_GROUPS_GRID_REFRESHED] : (
    {add, edit, audit, destroy, bulkImport}: AdminGroupsState
  ) => {
    add.result        = null;
    edit.result       = null;
    audit.result      = null;
    destroy.result    = null;
    bulkImport.result = null;
  }
};

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: AdminGroupsState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
